import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';

import { resolveApiMessage, showToast } from '../../../helpers';
import {
  addOpsProgramMember,
  searchOpsContacts,
  setOpsProgramMember,
} from '../../../requests';
import { TOpsProgramDetailEx } from '../../../requests/opsRegistrationsExtra';
import {
  btnGhost,
  Card,
  inputClass,
  Pill,
  SortTh,
  tableClass,
  tdClass,
  theadClass,
  thClass,
  useListSort,
} from '../ui';

/** Who may book against the program; add by contact search, enable/disable. */
const MembersCard: React.FC<{
  programId: string;
  program: TOpsProgramDetailEx;
  // The Status header shares the program list's sort (as it always has)
  sort: ReturnType<typeof useListSort>;
}> = ({ programId, program, sort }) => {
  const queryClient = useQueryClient();
  const { sortKey, sortDir, toggleSort } = sort;
  const [contactQuery, setContactQuery] = useState('');
  const { data: contactHits = [] } = useQuery({
    queryKey: ['opsContacts', contactQuery],
    queryFn: () => searchOpsContacts(contactQuery),
    enabled: Boolean(programId) && contactQuery.trim().length >= 2,
  });

  const refreshDetail = () =>
    queryClient.invalidateQueries({ queryKey: ['opsProgram', programId] });

  const addMember = useMutation({
    mutationFn: (contactId: string) =>
      addOpsProgramMember(programId, contactId),
    onSuccess: () => {
      showToast('Member added', 'success');
      setContactQuery('');
      refreshDetail();
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  const toggleMember = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      setOpsProgramMember(id, enabled),
    onSuccess: () => refreshDetail(),
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  return (
    <Card title="Program members">
      <div className="border-b border-white-three px-5 py-3">
        <div className="max-w-xl">
          <input
            className={`${inputClass} w-full`}
            placeholder="Add a member — search contacts by name or email…"
            value={contactQuery}
            onChange={(e) => setContactQuery(e.target.value)}
          />
          {contactHits.length > 0 && (
            <div className="mt-1 max-h-48 overflow-y-auto rounded-md border border-white-regular bg-white shadow-light">
              {contactHits.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="block w-full px-3 py-1.5 text-left text-sm transition-colors hover:bg-blue-100/40"
                  onClick={() => addMember.mutate(c.id)}
                >
                  {c.name}
                  {c.email && (
                    <span className="ml-2 text-xs text-gray-bold">
                      {c.email}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className={`${tableClass} min-w-[700px]`}>
          <thead className={theadClass}>
            <tr>
              <th className={thClass}>Member</th>
              <th className={thClass}>Email</th>
              <SortTh
                k="status"
                label="Status"
                sort={sortKey}
                dir={sortDir}
                onSort={toggleSort}
              />
              <th className={`${thClass} text-right`}>Classes in window</th>
              <th className={thClass}></th>
            </tr>
          </thead>
          <tbody>
            {program.members.map((m) => (
              <tr key={m.id}>
                <td className={`${tdClass} font-medium text-gray-four`}>
                  {m.name ?? '—'}
                  {m.localOrigin && (
                    <span className="ml-2">
                      <Pill value="added here" tone="info" />
                    </span>
                  )}
                </td>
                <td className={tdClass}>{m.email ?? '—'}</td>
                <td className={tdClass}>
                  <Pill value={m.status} />
                </td>
                <td className={`${tdClass} text-right`}>{m.classesInWindow}</td>
                <td className={`${tdClass} text-right`}>
                  <button
                    className={btnGhost}
                    onClick={() =>
                      toggleMember.mutate({
                        id: m.id,
                        enabled: !m.enabled,
                      })
                    }
                  >
                    {m.enabled ? 'Disable' : 'Enable'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default MembersCard;
